import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient as defaultQueryClient } from '@src/lib/query-client';
import { createHeaderStore, createSettingsStore } from '@src/redux/store';
import { Provider as ReduxProvider } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { clientTheme, createEmotionCache } from '@src/lib/mui/mui';
import PropTypes from 'prop-types';
import { hydrateRoot } from 'react-dom/client';
import ReactDOM from 'react-dom';
import { pick } from 'lodash';

// To deprecate at the end
export function renderClientWithProviders(component, store) {
    store = store || createSettingsStore(window.REDUX_DATA);

    return (
        <ReduxProvider store={store}>
            <QueryClientProvider client={defaultQueryClient}>{component}</QueryClientProvider>
        </ReduxProvider>
    );
}

// To deprecate at the end
export function renderClientWithMUI(Component, store) {
    const emotionCache = createEmotionCache();
    store = store || createSettingsStore(window.REDUX_DATA);

    return (
        <CacheProvider value={emotionCache}>
            <ThemeProvider theme={clientTheme}>
                <CssBaseline />
                {store ? <ReduxProvider store={store}>{Component}</ReduxProvider> : Component}
            </ThemeProvider>
        </CacheProvider>
    );
}

export const AppProvider = ({
    children,
    enableRedux = true,
    enableQueryClient = true,
    enableMUI = true,
    store,
    queryClient,
    emotionCache,
    theme
}) => {
    const resolvedQueryClient = queryClient || defaultQueryClient;
    const resolvedEmotionCache = emotionCache || createEmotionCache();
    const resolvedTheme = theme || clientTheme;

    let content = (
        <HelmetProvider>
            <Helmet>
                <meta
                    name='description'
                    content='Need help around the home? Chores, repairs, and more, help is just a click away. Search, book, and pay in minutes.'
                />
            </Helmet>
            {children}
        </HelmetProvider>
    );

    if (enableRedux) {
        content = <ReduxProvider store={store}>{content}</ReduxProvider>;
    }

    if (enableQueryClient) {
        content = <QueryClientProvider client={resolvedQueryClient}>{content}</QueryClientProvider>;
    }

    if (enableMUI) {
        content = (
            <CacheProvider value={resolvedEmotionCache}>
                <ThemeProvider theme={resolvedTheme}>
                    <CssBaseline />
                    {content}
                </ThemeProvider>
            </CacheProvider>
        );
    }

    return <>{content}</>;
};

AppProvider.propTypes = {
    children: PropTypes.node.isRequired,
    enableRedux: PropTypes.bool,
    enableQueryClient: PropTypes.bool,
    enableMUI: PropTypes.bool,
    store: PropTypes.object,
    queryClient: PropTypes.object,
    emotionCache: PropTypes.object,
    theme: PropTypes.object
};

export function renderComponent(Component, options = {}) {
    const {
        appId = 'root',
        store: componentStore = undefined,
        queryClient = undefined,
        emotionCache = undefined,
        theme = undefined,
        enableRedux = true,
        enableQueryClient = true,
        enableMUI = true,
        useHydrateRoot = false
    } = options;

    const app = document.getElementById(appId);

    const headerStore = createHeaderStore(
        pick(window.REDUX_DATA, ['userReducer', 'panelsReducer', 'marketplaceReducer', 'inboxReducer', 'cartReducer'])
    );

    const store = useHydrateRoot
        ? {
              ...headerStore,
              ...componentStore
          }
        : componentStore;

    if (!app) {
        console.error(`renderClient: Element with id="${appId}" not found.`);
        return;
    }

    if (useHydrateRoot) {
        hydrateRoot(
            app,
            <AppProvider
                store={store}
                queryClient={queryClient}
                emotionCache={emotionCache}
                theme={theme}
                enableRedux={enableRedux}
                enableQueryClient={enableQueryClient}
                enableMUI={enableMUI}
            >
                {Component}
            </AppProvider>
        );
    } else {
        // eslint-disable-next-line react/no-deprecated
        ReactDOM.hydrate(
            <AppProvider
                store={store}
                queryClient={queryClient}
                emotionCache={emotionCache}
                theme={theme}
                enableRedux={enableRedux}
                enableQueryClient={enableQueryClient}
                enableMUI={enableMUI}
            >
                {Component}
            </AppProvider>,
            app
        );
    }
}
